import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import PublicShell from "@/components/PublicShell";
import { api } from "@/lib/api";
import { useMoney } from "@/context/CurrencyContext";
import { useCart } from "@/context/CartContext";
import { CheckCircle2, Clock, XCircle } from "lucide-react";

export default function CheckoutSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "polling", data: null });
  const money = useMoney();
  const { clear } = useCart();

  useEffect(() => {
    if (!sessionId) { setState({ status: "error" }); return; }
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") { clear(); setState({ status: "paid", data }); return; }
        if (data.status === "expired") { setState({ status: "expired", data }); return; }
        attempts += 1;
        if (attempts >= 8) { setState({ status: "pending", data }); return; }
        setTimeout(poll, 2000);
      } catch {
        setState({ status: "error" });
      }
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <PublicShell>
      <section className="max-w-2xl mx-auto px-6 md:px-12 py-24 text-center">
        {state.status === "polling" && (
          <>
            <Clock size={40} className="mx-auto mb-6 text-mute" />
            <h1 className="font-serif text-4xl font-light mb-4 text-ink">Confirming your payment…</h1>
            <p className="text-ink-soft">This should take just a moment.</p>
          </>
        )}
        {state.status === "paid" && (
          <>
            <CheckCircle2 size={40} className="mx-auto mb-6 text-emerald-800" />
            <h1 className="font-serif text-5xl font-light mb-4 text-ink">Thank you.</h1>
            <div className="max-w-md mx-auto mb-8 bg-cream-soft border border-ink-10 p-6 text-left">
              <div className="overline text-mute mb-3">Order Receipt</div>
              {(state.data.items && state.data.items.length > 0 ? state.data.items : [{ artwork_title: state.data.artwork_title, size: state.data.size, quantity: 1, unit_price: (state.data.amount_total || 0) / 100 }]).map((it) => (
                <div key={`${it.artwork_id || it.artwork_title}-${it.size}-${it.material || ""}-${it.frame_color || ""}`} className="flex justify-between py-2 border-b border-ink-10 text-sm">
                  <div>
                    <div className="font-medium">{it.artwork_title}</div>
                    <div className="text-xs text-mute">{it.size} · Qty {it.quantity || 1}</div>
                  </div>
                  <div>{money((it.unit_price || 0) * (it.quantity || 1))}</div>
                </div>
              ))}
              <div className="flex justify-between pt-4 font-serif text-xl">
                <span>Total</span>
                <span>{money((state.data.amount_total || 0) / 100)}</span>
              </div>
            </div>
            <p className="text-sm text-mute mb-10">Jason will reach out within 24 hours with shipping details.</p>
            <Link to="/gallery" className="btn-ink" data-testid="success-continue">Continue Browsing</Link>
          </>
        )}
        {state.status === "pending" && (
          <>
            <Clock size={40} className="mx-auto mb-6 text-amber-700" />
            <h1 className="font-serif text-4xl font-light mb-4 text-ink">Payment still processing</h1>
            <p className="text-ink-soft mb-8">This is taking longer than expected. We&apos;ll notify you as soon as it clears.</p>
            <Link to="/" className="btn-outline">Back to Home</Link>
          </>
        )}
        {state.status === "expired" && (
          <>
            <XCircle size={40} className="mx-auto mb-6 text-red-800" />
            <h1 className="font-serif text-4xl font-light mb-4 text-ink">Payment session expired</h1>
            <Link to="/gallery" className="btn-ink">Try Again</Link>
          </>
        )}
        {state.status === "error" && (
          <>
            <XCircle size={40} className="mx-auto mb-6 text-red-800" />
            <h1 className="font-serif text-4xl font-light mb-4 text-ink">Something went wrong</h1>
            <Link to="/contact" className="btn-outline">Contact the Studio</Link>
          </>
        )}
      </section>
    </PublicShell>
  );
}
